/**
 * Booth OS · shared kiosk primitives.
 *
 * Lightweight tokens + atoms used by every in-kiosk capture flow
 * (Photo, Video, Guestbook, Memory Snap, Attract). Keeping these in
 * one place lets each flow stay short and lets the theme system
 * (5 booth atmospheres) thread through unchanged.
 *
 * Theme prop shape (from /lib/boothOSThemes.js):
 *   { bg, bgHi, paper, ink, inkMuted, inkFaint, gold, goldDeep, hair,
 *     polaroidFrame, polaroidTape, label, eyebrow, description, id }
 */
import React from 'react';

export const SERIF = 'Georgia, "Times New Roman", serif';

export const Eyebrow = ({ theme, children }) => (
  <div style={{
    fontFamily: SERIF, fontWeight: 800,
    fontSize: 12, letterSpacing: '0.32em', textTransform: 'uppercase',
    color: theme.gold, marginBottom: 18,
  }}>{children}</div>
);

export const Heading = ({ theme, children, size = 'md' }) => (
  <h1 style={{
    fontFamily: SERIF, fontWeight: 500,
    fontSize: size === 'lg' ? 'clamp(34px, 5vw, 56px)' : 'clamp(26px, 4vw, 40px)',
    lineHeight: 1.08, letterSpacing: '-0.012em',
    color: theme.ink, margin: 0,
  }}>{children}</h1>
);

export const Lede = ({ theme, children, center = true, maxWidth = 540 }) => (
  <p style={{
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 'clamp(15px, 1.4vw, 18px)',
    color: theme.inkMuted, lineHeight: 1.55,
    margin: '14px 0 0', maxWidth,
    textAlign: center ? 'center' : 'left',
    marginLeft: center ? 'auto' : 0,
    marginRight: center ? 'auto' : 0,
  }}>{children}</p>
);

export const BackLink = ({ theme, onClick, testId, children = '← Back' }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId || 'flow-back'}
    style={{
      background: 'transparent', border: 'none', cursor: 'pointer',
      color: theme.inkMuted, fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 14, letterSpacing: '0.04em', padding: 8,
    }}
  >{children}</button>
);

/** The gold-gradient kiosk CTA. Secondary variant is a parchment
    ghost pill. Both yield on press — no hover lifts. */
export const GoldPill = ({
  theme, onClick, children, large, secondary, testId, disabled,
  style, type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    style={{
      padding: large ? '18px 32px' : '14px 24px',
      borderRadius: 999, border: 'none',
      background: secondary
        ? 'transparent'
        : `linear-gradient(135deg, ${theme.gold} 0%, ${theme.goldDeep} 100%)`,
      color: secondary ? theme.ink : '#0F0B1A',
      fontFamily: SERIF, fontWeight: secondary ? 500 : 700,
      fontStyle: secondary ? 'italic' : 'normal',
      fontSize: large ? 18 : 15,
      letterSpacing: '0.02em',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      boxShadow: secondary ? 'none' : '0 10px 30px rgba(0,0,0,0.42)',
      transition: 'transform 220ms ease, background 220ms ease',
      ...(secondary ? { border: `1px solid ${theme.hair}` } : {}),
      ...style,
    }}
    onPointerDown={(e) => { if (!disabled) e.currentTarget.style.transform = 'translateY(1px)'; }}
    onPointerUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
    onPointerLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
  >
    {children}
  </button>
);

/** Frame every in-kiosk flow with a consistent header + body layout.
    Keeps padding, animation, and the back-link alignment uniform across
    Photo, Video, Guestbook, and Memory Snap. */
export const FlowFrame = ({ theme, onBack, eyebrow, children, testId }) => (
  <div
    data-testid={testId}
    style={{
      minHeight: '100vh',
      display: 'flex', flexDirection: 'column',
      padding: 'clamp(24px, 4vw, 44px) clamp(20px, 4vw, 48px)',
      animation: 'editorialFade .5s ease-out',
    }}
  >
    <header style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 16, flexWrap: 'wrap',
    }}>
      <BackLink theme={theme} onClick={onBack} />
      <Eyebrow theme={theme}>{eyebrow}</Eyebrow>
      <span style={{ width: 60 }} aria-hidden />
    </header>
    {children}
  </div>
);
